// Presentation mapping for a single live astrologer record
// (GET /astrologers/:id) → the props the details page sections render.

/** Photo-fallback gradients — same palette used across astrologer cards. */
const FALLBACK_GRADIENTS = [
  'linear-gradient(160deg,#FDE68A 0%,#F59E0B 50%,#D97706 100%)',
  'linear-gradient(160deg,#FDE8D8 0%,#F4A261 50%,#E76F51 100%)',
  'linear-gradient(160deg,#FEF3C7 0%,#FBBF24 50%,#D97706 100%)',
  'linear-gradient(160deg,#EDE9FE 0%,#A78BFA 50%,#7C3AED 100%)',
]

const formatPrice = (rate) => {
  const value = Number(rate)
  if (!Number.isFinite(value) || value <= 0) return 'Free'
  return `₹${value.toLocaleString('en-IN')}`
}

/** Unwrap the API envelope — the single-astrologer endpoint may return the
 * record directly or wrapped as `{ status, data }` like the list/filter ones. */
export function unwrapAstrologerRecord(response) {
  if (!response) return null
  if (typeof response === 'object' && 'data' in response && response.data && typeof response.data === 'object') {
    return response.data
  }
  return response
}

/** Shape a live astrologer record into the props the details page renders. */
export function decorateAstrologerDetail(record) {
  const name = record.display_name || record.name || 'Astrologer'
  const rating = Number(record.rating ?? 0)
  const online = record.online_status ? record.online_status === 'online' : true
  const skills = record.skills ?? []
  const languages = record.languages ?? []
  const price = Number(record.rate) || 0
  const experience = Number(record.experience) || 0
  const duration = Number(record.duration) || 30
  const reviews = record.reviews_count ?? 0
  // Astrologer's own registered mode (video/call/chat, per the astrologer
  // registration form) — astrologers can register multiple, comma-joined;
  // the first one is the default offered on the booking page. NOT the same
  // enum as the booking-create payload's consultation_type — see
  // toBookingConsultationType() below.
  const consultationMode = (record.consultation_mode || 'video').split(',')[0].trim()

  return {
    id: record.id,
    name,
    consultationMode,
    role: skills[0] ?? 'Vedic Astrologer',
    image: record.image_url || null,
    fallbackGradient: FALLBACK_GRADIENTS[Number(record.id ?? 0) % FALLBACK_GRADIENTS.length],
    fallbackLetter: name.charAt(0).toUpperCase(),
    online,
    status: online ? 'live' : 'busy',
    rating,
    reviews,
    skills,
    languages,
    experience,
    duration,
    price,
    priceLabel: formatPrice(price),
    ratingLabel: rating > 0 ? rating.toFixed(1) : 'New',
  }
}

/**
 * POST /bookings' `consultation_type` only accepts video|phone|inperson
 * (verified against the Postman collection) — a different enum from the
 * astrologer's own registered consultation_mode (video/call/chat). Map
 * between the two rather than forwarding the astrologer's mode as-is.
 */
export function toBookingConsultationType(consultationMode) {
  if (consultationMode === 'phone' || consultationMode === 'inperson') return consultationMode
  if (consultationMode === 'call') return 'phone'
  return 'video'
}
